/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 ~ Copyright 2020 Adobe Systems Incorporated
 ~
 ~ Licensed under the Apache License, Version 2.0 (the "License");
 ~ you may not use this file except in compliance with the License.
 ~ You may obtain a copy of the License at
 ~
 ~     http://www.apache.org/licenses/LICENSE-2.0
 ~
 ~ Unless required by applicable law or agreed to in writing, software
 ~ distributed under the License is distributed on an "AS IS" BASIS,
 ~ WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 ~ See the License for the specific language governing permissions and
 ~ limitations under the License.
 ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

import React, { useRef } from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager'
import {
  ScrollingBanner,
  useResponsiveRender,
  Video
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'
import Container from '../Container/Container'
import EditionWrapper from '../EditionWrapper'
import { AdaptiveScrollingBanner, VideoBannerModelProps } from './types'
import { EditableComponentPropsType } from '../../../utils/prop-types'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'

export const VideoBannerEditConfig = {
  emptyLabel: 'Video Banner',
  isEmpty: true
}

const AdaptativeScrollingBanner: React.FC<AdaptiveScrollingBanner> =
  withComponentRefresh((props: AdaptiveScrollingBanner) => {
    const { model, onMediaInViewChange, videoRef, media, background } = props
    const bannerConfig = {
      onMediaInViewChange,
      ref: videoRef,
      media,
      background,
      onBannerScrollingAttachmentChange: () => {}
    }
    return (
      <ScrollingBanner {...bannerConfig}>
        {model?.cqItems &&
          Object.keys(model?.cqItems)?.map((cqItemKey, index) => {
            const {
              ':items': cqItems,
              ':itemsOrder': cqItemsOrder,
              ':type': cqType,
              ...itemRest
            } = model.cqItems[cqItemKey]
            const elementConfig = {
              model: {
                cqItems,
                cqItemsOrder,
                cqType,
                ...itemRest
              },
              key: index
            }
            return <Container {...elementConfig} />
          })}
      </ScrollingBanner>
    )
  })

const VideoBanner: React.FC<EditableComponentPropsType<VideoBannerModelProps>> =
  withComponentRefresh((props) => {
    const { model } = props
    const {
      landscapeUrl,
      portraitUrl,
      id,
      background,
      enableLazyLoad,
      ...rest
    } = model
    const editableProps = useExtractEditableProps(props, rest)
    const mediaRef = useRef<HTMLVideoElement>()
    const onVideoInViewChange = (isMediaInView: boolean, videoSrc: string) => {
      if (isMediaInView && mediaRef?.current && !mediaRef.current.src) {
        mediaRef.current.src = videoSrc
      }
      if (isMediaInView) {
        mediaRef?.current?.play?.()
      } else {
        mediaRef?.current?.pause?.()
      }
    }
    const onMobileVideoInViewChange = (isMediaInView: boolean) => {
      onVideoInViewChange(isMediaInView, portraitUrl)
    }
    const onDesktopVideoInViewChange = (isMediaInView: boolean) => {
      onVideoInViewChange(isMediaInView, landscapeUrl)
    }
    const videoConfig = {
      innerRef: mediaRef,
      autoPlay: true,
      loop: true,
      muted: true,
      controls: false,
      playsInline: true,
      lazyLoading: enableLazyLoad,
      width: '100%',
      height: '100%',
      disablePictureInPicture: true,
      onLoadEnd: () => {},
      id,
      sx: { objectFit: 'cover' }
    }
    const config = {
      media: <Video {...videoConfig} />,
      videoRef: mediaRef,
      background,
      model: { ...rest }
    }
    const mobileConfig = {
      ...config,
      onMediaInViewChange: onMobileVideoInViewChange
    }
    const desktopConfig = {
      ...config,
      onMediaInViewChange: onDesktopVideoInViewChange
    }
    const render =
      useResponsiveRender([
        <AdaptativeScrollingBanner {...mobileConfig} />,
        null,
        <AdaptativeScrollingBanner {...desktopConfig} />
      ]) ?? undefined

    return (
      <EditableComponent {...editableProps}>
        {AuthoringUtils.isInEditor() ? (
          <EditionWrapper label={VideoBannerEditConfig.emptyLabel}>
            <Container {...editableProps} />
          </EditionWrapper>
        ) : (
          render
        )}
      </EditableComponent>
    )
  })

const VideoBannerWithErrorBoundary = withErrorBoundary(VideoBanner, {
  componentName: 'VideoBanner',
  fallback: <ErrorFallback componentName="VideoBanner" />
})

export default VideoBannerWithErrorBoundary
